import api from 'api';
import { createSlice } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import { IAlertItems, IGeneratedAlertItems } from 'pages/private/alerts/types';
import {
  ICreateEditOfferAndTipRequest,
  IGetOfferAndTipsRequest,
  IOfferAndTipsItem,
  IOfferNotifyUser,
  IProgrammedAlertItem,
  IProgrammedAlertsRequest,
} from 'interfaces/alerts-campaigns-interface';
import { getItemFromLocalStorage } from 'helpers/localstorage';
import axios from 'axios';
import { getLanguage } from 'helpers/i18n';
import toast from 'react-hot-toast';
import { IUploadImageRequestParam } from './organizationSlice';
import { IVehicleAlertItem } from 'interfaces/vehicle-interface';

export interface IAlertDropdownOption {
  id: string;
  name: string;
  value?: string;
}

export interface IAlertTemplateItem {
  id?: string;
  title: string;
  description: string;
  _id?: string;
  referenceCode: string;
}
interface IAlertsState {
  brands: IAlertDropdownOption[];
  modelsAndYears: IAlertDropdownOption[];
  versions: IAlertDropdownOption[];
  engines: IAlertDropdownOption[];
  alertsTemplates: IAlertTemplateItem[];
  selectedAlert: IAlertTemplateItem | null;
  alertsList: IAlertItems[];
  programmedAlerts: IProgrammedAlertItem[];
  selectedProgrammedAlert: IProgrammedAlertItem | null;
  generatedAlertsList: IGeneratedAlertItems[];
  offersAndTips: IOfferAndTipsItem[];
  offerNotifyUsers: IOfferNotifyUser[];
  vehicleAlertDetail: IVehicleAlertItem | null;
  totalAlertCount?: number;
  totalOfferAndTipsCount?: number;
  totalGeneratedAlertsCount: number;
  totalProgrammedAlertsCount: number;
  generatedAlert: IGeneratedAlertItems | null;
}

export interface IGenerateAlertRequest {
  brand?: string;
  engine?: string;
  tireSize?: string;
  title: string;
  description: string;
  model?: string;
  year?: string;
}

interface IAlertItemResponse {
  alerts: IAlertItems[];
  record_count: number;
}

interface IOfferAndTipsItemResponse {
  offersAndTips: IOfferAndTipsItem[];
  record_count: number;
}
interface IGeneratedAlertItemsResponse {
  alerts: IGeneratedAlertItems[];
  record_count: number;
}

interface IProgrammedAlertItemResponse {
  alerts: IProgrammedAlertItem[];
  record_count: number;
}

export interface IEditAlertRequest {
  alert_update_km?: string;
  alert_update_date?: string;
}

const initialState: IAlertsState = {
  brands: [],
  modelsAndYears: [],
  versions: [],
  engines: [],
  alertsTemplates: [],
  selectedAlert: null,
  alertsList: [],
  programmedAlerts: [],
  selectedProgrammedAlert: null,
  generatedAlertsList: [],
  offersAndTips: [],
  offerNotifyUsers: [],
  vehicleAlertDetail: null,
  totalAlertCount: 0,
  totalOfferAndTipsCount: 0,
  totalGeneratedAlertsCount: 0,
  totalProgrammedAlertsCount: 0,
  generatedAlert: null,
};

const alertsSlice = createSlice({
  name: 'Alerts',
  initialState: initialState,
  reducers: {
    setModalAndYears: (state, { payload }: { payload: IAlertDropdownOption[] }) => {
      state.modelsAndYears = payload;
    },
    setBrands: (state, { payload }: { payload: IAlertDropdownOption[] }) => {
      state.brands = payload;
    },
    setVersions: (state, { payload }: { payload: IAlertDropdownOption[] }) => {
      state.versions = payload;
    },
    setEngines: (state, { payload }: { payload: IAlertDropdownOption[] }) => {
      state.engines = payload;
    },
    setAlertsTemplates: (state, { payload }: { payload: IAlertTemplateItem[] }) => {
      state.alertsTemplates = payload;
    },
    setSelectAlertTemplate: (state, { payload }: { payload: IAlertTemplateItem | null }) => {
      state.selectedAlert = payload;
    },
    setAlertsList: (state, { payload }: { payload: IAlertItemResponse }) => {
      state.alertsList = [...payload.alerts];
      state.totalAlertCount = payload.record_count;
    },
    setProgrammedAlertsTemplates: (
      state,
      { payload }: { payload: IProgrammedAlertItemResponse },
    ) => {
      state.programmedAlerts = [...payload.alerts];
      state.totalProgrammedAlertsCount = payload.record_count;
    },
    setSelectedtProgrammedAlert: (state, { payload }: { payload: IProgrammedAlertItem }) => {
      state.selectedProgrammedAlert = payload;
    },
    setGeneratedAlertsList: (state, { payload }: { payload: IGeneratedAlertItemsResponse }) => {
      state.generatedAlertsList = [...payload.alerts];
      state.totalGeneratedAlertsCount = payload.record_count;
    },
    setOfferTipsList: (state, { payload }: { payload: IOfferAndTipsItemResponse }) => {
      state.offersAndTips = [...payload.offersAndTips];
      state.totalOfferAndTipsCount = payload.record_count;
    },
    setOfferNotifyUsers: (state, { payload }: { payload: IOfferNotifyUser[] }) => {
      state.offerNotifyUsers = payload;
    },
    setVehicleAlertDetails: (state, { payload }: { payload: IVehicleAlertItem }) => {
      state.vehicleAlertDetail = payload;
    },
    setGeneratedAlert: (state, { payload }: { payload: IGeneratedAlertItems }) => {
      state.generatedAlert = payload;
    },
  },
});

export const getAlertsList =
  (
    payload: {
      offset?: number;
      limit?: number;
      brands?: string[];
      engines?: string[];
      front_tires?: string[];
      rear_tires?: string[];
      searchString?: string;
    },
    signal?: AbortSignal,
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { offset, limit, searchString, brands, engines, front_tires, rear_tires } = payload;
      let queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}`;
      if (searchString) {
        queryString += `&searchString=${encodeURIComponent(searchString)}`;
      }

      const { data: responseData } = await api.post(
        `/admin/alert/pref${queryString}`,
        {
          brands: brands,
          engines: engines,
          front_tires: front_tires,
          rear_tires: rear_tires,
        },
        { signal },
      );
      dispatch(setAlertsList(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

// export const getAlertsList = (offset: number, limit: number) => async (dispatch: Dispatch) => {
//   try {
//     const { data: responseData } = await api.get('/admin/alert/pref', {
//       params: { offset, limit },
//     });
//     dispatch(setAlertsList(responseData?.data?.alerts));
//     return Promise.resolve(responseData?.data?.alerts);
//   } catch (error) {
//     return Promise.reject(error);
//   }
// };
export const getModelsAndYearsOptions = () => async (dispatch: Dispatch) => {
  try {
    const { data: responseData } = await api.get('/admin/models');
    dispatch(setModalAndYears(responseData?.data));
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const getBrandsOptions = () => async (dispatch: Dispatch) => {
  try {
    const { data: responseData } = await api.get('/admin/brand');
    dispatch(setBrands(responseData?.data));
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const getVersionsOptions = () => async (dispatch: Dispatch) => {
  try {
    const { data: responseData } = await api.get('/admin/engine');
    dispatch(setEngines(responseData?.data));
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const getEnginesOptions = () => async (dispatch: Dispatch) => {
  try {
    const { data: responseData } = await api.get('/admin/version');
    dispatch(setVersions(responseData?.data));
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const getAlertsTemplates = () => async (dispatch: Dispatch) => {
  try {
    const { data: responseData } = await api.get('/admin/alert/template');
    dispatch(setAlertsTemplates(responseData?.data));
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const createAlertTemplates =
  (payload: { request: { title: string; description: string } }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const {
        data: responseData,
      }: {
        data: { data: { _id: string; title: string; description: string; referenceCode: string } };
      } = await api.post('/admin/alert/template', { ...request });
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const generateAlert = (payload: { request: FormData }) => async (dispatch: Dispatch) => {
  try {
    const { request } = payload;

    const org = getItemFromLocalStorage('org');
    const res = await axios
      .post(`${process.env.REACT_APP_BASE_URL}/admin/generate/alert`, request, {
        headers: {
          Authorization: org?.token,
          lang: getLanguage(),
          'Content-Type': 'multipart/form-data',
        },
      })
      .catch(err => {
        err?.response?.data?.error?.forEach((e: string) =>
          toast.error(e ? e : 'Something went wrong'),
        );
      });
    const responseData: {
      data: IAlertItems;
      message: string;
      success_code: string;
    } = res?.data || {};
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const removeAlertTemplate =
  (payload: { template_id: string }) => async (dispatch: Dispatch) => {
    try {
      const { template_id } = payload;
      const { data: responseData } = await api.delete(`/admin/alert/template/${template_id}`);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getProgrammedAlerts =
  (
    payload: {
      offset?: number;
      limit?: number;
      brands?: string[];
      engines?: string[];
      front_tires?: string[];
      rear_tires?: string[];
      searchString?: string;
    },
    signal?: AbortSignal,
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { offset, limit, searchString, brands, engines, front_tires, rear_tires } = payload;
      let queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}`;
      if (searchString) {
        queryString += `&searchString=${searchString}`;
      }
      const { data: responseData } = await api.post(
        `/admin/campaign/alert${queryString}`,
        {
          brands: brands,
          engines: engines,
          front_tires: front_tires,
          rear_tires: rear_tires,
        },
        { signal },
      );
      dispatch(setProgrammedAlertsTemplates(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const createProgrammedAlerts =
  (payload: { request: IProgrammedAlertsRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData } = await api.post('/admin/programmed/alert', request);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const updateProgrammedAlerts =
  (payload: { request: IProgrammedAlertsRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData } = await api.post(
        `/admin/programmed/alert/${request?.alert_id!}`,
        request,
      );
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const setSelectedProgrammedAlerts =
  (payload: { alert: IProgrammedAlertItem }) => async (dispatch: Dispatch) => {
    try {
      const { alert } = payload;
      dispatch(setSelectedtProgrammedAlert(alert));
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const removeProgrammedAlert =
  (payload: { alert_id: string }) => async (dispatch: Dispatch) => {
    try {
      const { alert_id } = payload;
      const { data: responseData } = await api.delete(`/admin/programmed/alert/${alert_id}`);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getGeneratedAlertsList =
  (
    payload: {
      params?: { status?: string[]; searchString?: string };
      url?: string;
      offset?: number;
      limit?: number;
    },
    signal?: AbortSignal,
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { params, url, offset, limit } = payload;
      const requestParams = params || {};
      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 10}${
        params?.searchString ? `&searchString=${encodeURIComponent(params.searchString)}` : ''
      }`;
      const finalUrl = url ? `${url}${queryString}` : `/admin/alerts${queryString}`;

      const { data: responseData } = await api.post(finalUrl, requestParams, { signal });
      dispatch(setGeneratedAlertsList(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const completeAlert = (payload: { alert_id: string }) => async (dispatch: Dispatch) => {
  try {
    const { alert_id } = payload;
    const { data: responseData } = await api.get(`/admin/alert/mark-as-complete/${alert_id}`);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const uploadOfferAndTipsMedia =
  (payload: { params: IUploadImageRequestParam }) => async (dispatch: Dispatch) => {
    try {
      const {
        params: { request, file },
      } = payload;
      const constfile = file;
      const { data: responseData } = await api.get('/admin/assets/get/offer-tip', {
        params: { ...request },
      });
      await axios.put(`${responseData?.data?.url}`, constfile);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const createOfferAndTips =
  (payload: { request: ICreateEditOfferAndTipRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;

      const { data: responseData } = await api.post('/admin/genrate/offer-and-tips', request);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getOfferAndTips =
  (payload: { request: IGetOfferAndTipsRequest; offset: number; limit: number }) =>
  async (dispatch: Dispatch) => {
    try {
      const { request, offset, limit } = payload;
      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}`;
      const { data: responseData } = await api.post(
        `/admin/list/offer-and-tips${queryString}`,
        request,
      );
      dispatch(setOfferTipsList(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getOfferNotifyUsers =
  (payload: { request: { offer_id: string } }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData } = await api.get(`/admin/offer-and-tips/${request?.offer_id}`);
      dispatch(setOfferNotifyUsers(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const resendOffer =
  (payload: { request: { offer_id: string } }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData } = await api.get(
        `/admin/resend/offer-and-tip/${request.offer_id}`,
      );
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const updateProgrammedALertStatus =
  (payload: { params: { alert_id: string; status: boolean } }) => async () => {
    try {
      const { params } = payload;
      const { data: responseData } = await api.get('/admin/programmed/alert/change/status', {
        params,
      });
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const editAlertKmOrDate =
  (payload: { alert_id: string; request: IEditAlertRequest }) => async () => {
    try {
      const { alert_id, request } = payload;
      const { data: responseData } = await api.post(`/admin/edit/alert/${alert_id}`, {
        ...request,
      });

      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const {
  setBrands,
  setEngines,
  setModalAndYears,
  setVersions,
  setAlertsTemplates,
  setSelectAlertTemplate,
  setAlertsList,
  setProgrammedAlertsTemplates,
  setSelectedtProgrammedAlert,
  setGeneratedAlertsList,
  setOfferTipsList,
  setOfferNotifyUsers,
  setVehicleAlertDetails,
  setGeneratedAlert,
} = alertsSlice.actions;
export default alertsSlice.reducer;
